import { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardFooter,
  CardHeader,
  Col,
  FormGroup,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupText,
  Label,
  Row,
} from "reactstrap";
import RightPanelCard from "components/Common/RightPanelCard";
import Select from "react-select";
import { useDispatch, useSelector } from "react-redux";
import { addUser, getUsersList, getRoleList } from "actions/users";
import { customStyle, REGEX } from "components/Common/constants";

const initialFields = {
  firstName: {
    value: "",
    error: "",
  },
  lastName: {
    value: "",
    error: "",
  },
  email: {
    value: "",
    error: "",
  },
  password: {
    value: "",
    error: "",
  },
  role: {
    value: "",
    error: "",
  },
};

function AddUserModal({
  isModalOpen,
  closeAddModal,
  notify,
  pageNum,
  setPageNum,
}) {
  const dispatch = useDispatch();
  const roleList = useSelector((state) => state.users.roleList); //user list form redux
  const [userFormData, setUserFormData] = useState(initialFields);
  const [showPassword, setShowPassword] = useState(false);
  const [showLoaderOnClick, setShowLoaderOnClick] = useState(false);

  useEffect(() => {
    getRoleList(dispatch);
  }, []);

  const handleInputChange = (fieldName, newValue) => {
    setUserFormData((prev) => ({
      ...prev,
      [fieldName]: {
        value: newValue,
        error: "",
      },
    }));
  };

  // based on the field name will retuen error if condition meets
  const getValidationError = (name, value) => {
    switch (name) {
      case "firstName":
        return !value
          ? "Please enter a first name"
          : value.length < 2
            ? "First name must be at least 2 characters"
            : value.length > 50
              ? "First name must be less than 50 characters"
              : "";

      case "lastName":
        return !value
          ? "Please enter a last name"
          : value.length < 2
            ? "Last name must be at least 2 characters"
            : value.length > 50
              ? "Last name must be less than 50 characters"
              : "";

      case "email":
        return !value
          ? "Please enter an email"
          : !REGEX.EMAIL_REGEX.test(value)
            ? "Please enter a valid email"
            : "";
      case "password":
        return !value
          ? "Please enter a password"
          : value.length < 8
            ? "The password must contain 8 characters"
            : "";
      case "role":
        return !value ? "Please select a role" : "";
      default:
        return "";
    }
  };

  //to validate form for all the keys in the userForm object
  const validateForm = () => {
    let newUser = { ...userFormData };

    // get all keys in array and then iterate
    Object.keys(newUser).forEach((key) => {
      newUser[key] = {
        ...newUser[key],
        error: getValidationError(key, newUser[key].value),
      };
    });

    setUserFormData(newUser); // set the error state

    //if no error exisit then return true else false
    return Object.values(newUser).every((item) => item.error === "");
  };

  const handleSaveClick = async () => {
    if (validateForm()) {
      try {
        setShowLoaderOnClick(true);
        // bodyData which will se sent to api
        const bodyData = {
          firstName: userFormData.firstName.value,
          lastName: userFormData.lastName.value,
          email: userFormData.email.value,
          password: userFormData.password.value,
          roleID: userFormData.role.value.ID,
        };
        const res = await addUser(bodyData);
        if (res.code === 200) {
          notify("success", res.message || "User Added successful");
          closeAddModal();
          //list call on successfull add
          if (pageNum === 1) {
            getUsersList(dispatch, pageNum);
          } else {
            setPageNum(1);
          }
        } else {
          const message = Array.isArray(res?.message)
            ? res?.message[0]?.Msg
            : res?.message || "Failed to Add User!";
          notify("danger", message);
        }
      } catch (e) {
        notify("danger", "Failed to Add User!");
      } finally {
        setShowLoaderOnClick(false);
      }
    }
  };

  //on blur to get if any validation error exits
  const handleOnBlur = (name, value) => {
    const trimmedValue = typeof value === "string" ? value.trim() : value;
    const error = getValidationError(name, trimmedValue);
    setUserFormData((prev) => ({
      ...prev,
      [name]: {
        ...prev[name],
        value: trimmedValue,
        error,
      },
    }));
  };

  return (
    <RightPanelCard
      isOpen={isModalOpen}
      onClose={closeAddModal}
      // width={"50vw"}
    >
      <Card className="mb-0">
        <CardHeader
          className="d-flex align-items-center justify-content-between mb-2"
          tag="h4"
        >
          Add User
          <i
            className="fa fa-close text-muted pointer"
            onClick={closeAddModal}
          />
        </CardHeader>
        <CardBody>
          <Row>
            <Col md="6">
              <FormGroup
                className={`has-label ${
                  userFormData.firstName.error ? "has-danger" : ""
                } d-flex flex-column align-items-start`}
              >
                <Label>
                  First Name <span className="validation-error">*</span>
                </Label>
                <Input
                  name="firstName"
                  type="text"
                  placeholder="Enter first name"
                  autoComplete="off"
                  value={userFormData.firstName.value}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  required
                  maxLength={50}
                />
                {userFormData.firstName.error && (
                  <small className="validation-error">
                    {userFormData.firstName.error}
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup
                className={`has-label ${
                  userFormData.lastName.error ? "has-danger" : ""
                } d-flex flex-column align-items-start`}
              >
                <Label>
                  Last Name <span className="validation-error">*</span>
                </Label>
                <Input
                  name="lastName"
                  type="text"
                  placeholder="Enter last name"
                  autoComplete="off"
                  value={userFormData.lastName.value}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  required
                  maxLength={50}
                />
                {userFormData.lastName.error && (
                  <small className="validation-error">
                    {userFormData.lastName.error}
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup
                className={`has-label ${
                  userFormData.email.error ? "has-danger" : ""
                } d-flex flex-column align-items-start`}
              >
                <Label for="email">
                  Email Address <span className="validation-error">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Enter email address"
                  autoComplete="off"
                  value={userFormData.email.value}
                  onChange={(e) =>
                    handleInputChange(e.target.name, e.target.value)
                  }
                  onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                  required
                  maxLength={100}
                />
                {userFormData.email.error && (
                  <small className="validation-error">
                    {userFormData.email.error}
                  </small>
                )}
              </FormGroup>
            </Col>
            <Col md="6">
              <FormGroup
                className={`has-label ${
                  userFormData.password.error ? "has-danger" : ""
                } d-flex flex-column align-items-start`}
              >
                <Label for="password">
                  Password <span className="validation-error">*</span>
                </Label>
                <InputGroup className="input-group">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="off"
                    placeholder="Enter password"
                    value={userFormData.password.value}
                    onChange={(e) =>
                      handleInputChange(e.target.name, e.target.value)
                    }
                    onBlur={(e) => handleOnBlur(e.target.name, e.target.value)}
                    required
                    maxLength={16}
                  />

                  <InputGroupAddon addonType="append">
                    <InputGroupText
                      className="d-flex align-items-center pointer"
                      onClick={() => setShowPassword((prev) => !prev)}
                    >
                      {showPassword ? (
                        <i className="fa fa-eye-slash" />
                      ) : (
                        <i className="fa fa-eye" />
                      )}
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
                {userFormData.password.error && (
                  <small className="validation-error">
                    {userFormData.password.error}
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
          <Row>
            <Col md="6">
              <FormGroup className="d-flex flex-column align-items-start">
                <Label>
                  Role <span className="validation-error">*</span>
                </Label>
                <Select
                  className={`text-left ${
                    userFormData.role.error ? "is-invalid" : ""
                  }`}
                  classNamePrefix="react-select"
                  options={roleList?.data?.role}
                  placeholder="Select role"
                  styles={customStyle}
                  value={userFormData.role.value}
                  getOptionLabel={(options) => options.name}
                  getOptionValue={(options) => options.ID}
                  onChange={(selected) => handleInputChange("role", selected)}
                  onBlur={() => handleOnBlur("role", userFormData.role.value)}
                  menuPortalTarget={document.body}
                  menuPosition={"fixed"}
                />
                {userFormData.role.error && (
                  <small className="validation-error">
                    {userFormData.role.error}
                  </small>
                )}
              </FormGroup>
            </Col>
          </Row>
        </CardBody>
        <CardFooter className="d-flex justify-content-end">
          <Button color="secondary" onClick={closeAddModal} className="mr-2">
            Cancel
          </Button>
          <Button
            color="default"
            type="submit"
            onClick={() => {
              handleSaveClick();
            }}
            disabled={Object.values(userFormData).some(
              (item) => item.error !== ""
            )}
          >
            {showLoaderOnClick ? (
              <i className="fa fa-spinner fa-spin fa-lg text-white" />
            ) : (
              "Save"
            )}
          </Button>
        </CardFooter>
      </Card>
    </RightPanelCard>
  );
}

export default AddUserModal;
